import type { FocusType, PresentationSession, SessionEvent, SessionStatus } from './types';

/**
 * Pure reducer voor SSE-events op een PresentationSession. Geëxtraheerd uit
 * LiveControlPage.tsx/LivePresenterPage.tsx: de twee bestaande switches waren
 * letterlijk identiek (geen bewust gedupliceerde logica, gewoon dezelfde
 * pure functie twee keer inline) -- de nieuwe presentation.focus_changed-case
 * is aanzienlijk complexer dan de bestaande one-liners, dus gedupliceerd
 * laten verdubbelt het risico op drift.
 *
 * is_overview is bewust GEEN onderdeel van deze reducer: dat is ephemere
 * weergavestate, geen PresentationSession-veld -- elke pagina houdt dat in
 * een eigen useState<boolean>, bijgewerkt in dezelfde onEvent-callback naast
 * setSession.
 *
 * Volgorde (Live Reliability Fase 1): de backend voorziet elk toestands-
 * wijzigend event van `state_version` (monotoon per sessie). Een event met
 * een versie die niet nieuwer is dan wat we al hebben (`eventIsStale`) wordt
 * genegeerd, zodat een laat aankomend of dubbel event een nieuwere status
 * nooit terugzet. Events zonder versie (oudere backend) worden zoals voorheen
 * altijd toegepast. `presentation.state` is de volledige snapshot bij
 * (her)verbinden en vervangt de toestand in één keer.
 */

const TERMINAL_STATUSES: SessionStatus[] = ['beeindigd', 'verlopen'];

// Events die de sessietoestand delta-gewijs wijzigen en een versie dragen.
const DELTA_EVENT_TYPES = new Set([
  'presentation.controller.connected',
  'presentation.slide.changed',
  'presentation.focus_changed',
  'presentation.blackout.enabled',
  'presentation.blackout.disabled',
]);

function versionOf(event: SessionEvent): number | null {
  return typeof event.state_version === 'number' ? event.state_version : null;
}

function isTerminalStatus(status: unknown): status is SessionStatus {
  return TERMINAL_STATUSES.includes(status as SessionStatus);
}

/** Is dit event ouder dan (of gelijk aan, voor delta's) de toestand die we al
 * hebben? Eindstatussen zijn nooit "oud": beëindigd blijft beëindigd. */
export function eventIsStale(session: PresentationSession | null, event: SessionEvent): boolean {
  if (!session) return false;
  const version = versionOf(event);
  if (version === null) return false;
  const current = session.state_version ?? 0;
  if (event.type === 'presentation.state') {
    return !isTerminalStatus(event.status) && version < current;
  }
  if (DELTA_EVENT_TYPES.has(event.type)) return version <= current;
  return false;
}

/** Wijst dit event op gemiste updates? Een delta met een gat in de
 * versiereeks, of een ping met een hogere versie dan wij hebben: dan moet de
 * verbinding opnieuw geopend worden om de actuele snapshot op te halen. */
export function eventRevealsMissedState(session: PresentationSession | null, event: SessionEvent): boolean {
  if (!session) return false;
  const version = versionOf(event);
  if (version === null) return false;
  const current = session.state_version ?? 0;
  if (event.type === 'presentation.ping') return version > current;
  if (DELTA_EVENT_TYPES.has(event.type)) return version > current + 1;
  return false;
}

function applyState(prev: PresentationSession, event: SessionEvent): PresentationSession {
  const version = versionOf(event);
  if (isTerminalStatus(event.status)) {
    return { ...prev, status: event.status, state_version: Math.max(version ?? 0, prev.state_version ?? 0) };
  }
  if (eventIsStale(prev, event)) return prev;
  return {
    ...prev,
    status: (event.status as SessionStatus | undefined) ?? prev.status,
    current_slide_id: (event.current_slide_id as string | null | undefined) ?? null,
    current_item_type: (event.current_item_type as FocusType | undefined) ?? prev.current_item_type,
    current_item_id: (event.current_item_id as string | null | undefined) ?? null,
    current_path_index: (event.current_path_index as number | undefined) ?? prev.current_path_index,
    canvas_manual_overview: (event.canvas_manual_overview as boolean | undefined) ?? prev.canvas_manual_overview,
    is_blackout: (event.is_blackout as boolean | undefined) ?? prev.is_blackout,
    state_version: version ?? prev.state_version,
  };
}

export default function applySessionEvent(prev: PresentationSession, event: SessionEvent): PresentationSession {
  switch (event.type) {
    case 'presentation.state':
      return applyState(prev, event);
    case 'presentation.ping':
      // Een ping verandert de slide nooit; alleen een eindstatus (sessie is
      // intussen beëindigd, bijv. tijdens een verbindingsonderbreking) telt.
      return isTerminalStatus(event.status) ? { ...prev, status: event.status } : prev;
    case 'presentation.session.ended':
      return { ...prev, status: 'beeindigd', state_version: Math.max(versionOf(event) ?? 0, prev.state_version ?? 0) };
    default:
      break;
  }

  if (eventIsStale(prev, event)) return prev;
  const stamped = (next: PresentationSession): PresentationSession => {
    const version = versionOf(event);
    return version === null ? next : { ...next, state_version: version };
  };

  switch (event.type) {
    case 'presentation.controller.connected':
      return stamped({ ...prev, status: 'actief' });
    case 'presentation.slide.changed':
      return stamped({ ...prev, current_slide_id: (event.slide_id as string | undefined) ?? prev.current_slide_id });
    case 'presentation.focus_changed':
      return stamped({
        ...prev,
        current_item_type: (event.focus_type as FocusType | undefined) ?? prev.current_item_type,
        current_item_id: (event.focus_id as string | null | undefined) ?? null,
        current_path_index: (event.path_index as number | undefined) ?? prev.current_path_index,
      });
    case 'presentation.blackout.enabled':
      return stamped({ ...prev, is_blackout: true });
    case 'presentation.blackout.disabled':
      return stamped({ ...prev, is_blackout: false });
    default:
      return prev;
  }
}
